import React, { useEffect, useState, useCallback, useMemo } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { endpoints } from '../api/config';
import { useCachedData } from '../hooks/useCachedData';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, ViewColumn as ViewColumnIcon, SaveAlt as SaveAltIcon, Close as CloseIcon, CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';
import { getDateInputProps } from '../utils/dateConstraints';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';

const HomedepotProductsErrors = () => {
  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();
  const actionTextSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: 'primary.main',
    cursor: 'pointer',
    userSelect: 'none',
    textDecoration: 'none',
    textUnderlineOffset: '2px',
    '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
  };

  // Default date range to today's date in YYYY-MM-DD
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const endpointWithRange = useMemo(() => {
    const base = endpoints.homeDepotProductsErrors;
    if (startDate && endDate) {
      return `${base}?start_date=${startDate}&end_date=${endDate}`;
    }
    return base;
  }, [startDate, endDate]);

  const { data: cachedData, isLoading, error, refetch } = useCachedData(endpointWithRange, {
    refetchInterval: 30000,
    staleTime: 20000,
  });

  // Flatten API data similar to products page
  const data = useMemo(() => {
    if (!cachedData) return [];
    const rows = [];
    const pushRow = (rec, skuFromKey, dateFromKey) => {
      const out = { ...rec };
      if (!out.sku && !out.sku_key && skuFromKey) out.sku_key = skuFromKey;
      if (!out.scraped_at_date && dateFromKey) out.scraped_at_date = dateFromKey;
      rows.push(out);
    };

    if (Array.isArray(cachedData)) {
      cachedData.forEach((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          rows.push({ ...item });
        }
      });
      return rows;
    }

    if (cachedData && typeof cachedData === 'object') {
      Object.entries(cachedData).forEach(([skuKey, value]) => {
        if (Array.isArray(value)) {
          value.forEach((entry) => {
            if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
              const dateKeys = Object.keys(entry);
              dateKeys.forEach((dateKey) => {
                const arr = entry[dateKey];
                if (Array.isArray(arr)) {
                  arr.forEach((rec) => pushRow(rec, skuKey, dateKey));
                } else if (arr && typeof arr === 'object') {
                  pushRow(arr, skuKey, dateKey);
                }
              });
            }
          });
        }
      });
      return rows;
    }
    return [];
  }, [cachedData]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [selectedVersion, setSelectedVersion] = useState('');

  const fetchData = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const keys = Object.keys(data[0]);
      setVisibleColumns((prev) => {
        const next = { ...prev };
        keys.forEach((k) => {
          if (typeof next[k] === 'undefined') next[k] = true;
        });
        return next;
      });
    }
  }, [data]);

  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchData();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleColumnMenuOpen = (e) => setColumnMenuAnchorEl(e.currentTarget);
  const handleColumnMenuClose = () => setColumnMenuAnchorEl(null);
  const toggleColumn = (key) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));

  const allKeys = useMemo(() => (Array.isArray(data) && data.length > 0 ? Object.keys(data[0]) : []), [data]);
  const visibleKeys = useMemo(() => allKeys.filter((k) => visibleColumns[k] !== false), [allKeys, visibleColumns]);

  const toHeader = useCallback((key) => key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '), []);



  const versionKey = useMemo(() => {
    const candidates = ['day_version', 'version', 'dayversion'];
    return allKeys.find((k) => candidates.includes(k.toLowerCase())) || null;
  }, [allKeys]);

  const availableVersions = useMemo(() => {
    if (!versionKey || !Array.isArray(data)) return [];
    const set = new Set();
    data.forEach((row) => {
      const v = row[versionKey];
      if (v !== undefined && v !== null && v !== '') set.add(String(v));
    });
    return Array.from(set).sort();
  }, [data, versionKey]);

  useEffect(() => {
    setSelectedVersion('');
  }, [startDate, endDate]);

  // Debounce search to reduce filtering cost
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const filteredRows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const lower = debouncedSearch.toLowerCase();
    const filtered = data.filter((row) => {
      const matchesSearch = lower ? allKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(lower)) : true;
      const matchesVersion = selectedVersion && versionKey ? String(row[versionKey]) === String(selectedVersion) : true;
      return matchesSearch && matchesVersion;
    });
    // Keep API order without extra sorting
    return filtered;
  }, [data, debouncedSearch, allKeys, selectedVersion, versionKey]);

  // Sort filtered rows by id (ascending)
  const sortedRows = useMemo(() => {
    if (!Array.isArray(filteredRows)) return [];
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const idA = a.id || a._id || 0;
      const idB = b.id || b._id || 0;
      const numA = typeof idA === 'number' ? idA : parseInt(idA, 10);
      const numB = typeof idB === 'number' ? idB : parseInt(idB, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return String(idA).localeCompare(String(idB));
    });
    return arr;
  }, [filteredRows]);

  const gridColumns = useMemo(
    () =>
      visibleKeys.map((key) => ({
        field: key,
        headerName: toHeader(key),
        resizable: true,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const val = params.row?.[key];
          if (val === null || val === undefined || val === '') return '-';
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        },
      })),
    [visibleKeys, toHeader]
  );
  const gridRows = useMemo(
    () =>
      sortedRows.map((row, idx) => ({
        ...row,
        id: row.id ?? row._id ?? row.sku_key ?? `hdpe-${idx}`,
      })),
    [sortedRows]
  );

  const handleDownloadCSV = () => {
    if (!sortedRows.length || visibleKeys.length === 0) return;
    const header = visibleKeys.join(',');
    const process = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val).replace(/"/g, '""');
      return String(val).replace(/"/g, '""');
    };
    const rows = sortedRows.map((row) => visibleKeys.map((k) => `"${process(row[k])}"`).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'homedepot_products_errors.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isSingleDay = useMemo(() => Boolean(startDate && endDate && startDate === endDate), [startDate, endDate]);

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1.5, fontSize: '1.25rem' }}>
          Home Depot Products Errors
        </Typography>

        <Box sx={{ 
          mb: 1.5, 
          display: 'flex', 
          alignItems: 'center',
          gap: 1,
          width: '100%'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
            <TextField
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.875rem' }
              }}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); }}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={getDateInputProps()}
              sx={{ 
                width: { xs: 140, sm: 160 },
                fontSize: '0.875rem',
                '& .MuiInputBase-root': {
                  height: '36px'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); }}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={getDateInputProps()}
              sx={{ 
                width: { xs: 140, sm: 160 },
                fontSize: '0.875rem',
                '& .MuiInputBase-root': {
                  height: '36px'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                }
              }}
            />
            <FormControl 
              sx={{ 
                minWidth: { xs: 140, sm: 160 },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                },
                '& .MuiOutlinedInput-root': {
                  height: '36px',
                  '& .MuiSelect-select': {
                    fontSize: '0.875rem',
                    paddingTop: '8px',
                    paddingBottom: '8px'
                  }
                }
              }} 
              size="small"
            >
              <InputLabel id="version-filter-label" sx={{ fontSize: '0.875rem' }}>Version</InputLabel>
              <Select
                labelId="version-filter-label"
                label="Version"
                value={selectedVersion}
                onChange={(e) => { setSelectedVersion(e.target.value); }}
                displayEmpty={false}
                disabled={!versionKey}
                sx={{ 
                  fontSize: '0.875rem'
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                  <ListItemText primary="All Versions" />
                </MenuItem>
                {availableVersions.map((v) => (
                  <MenuItem key={v} value={v} sx={{ fontSize: '0.875rem' }}>
                    <ListItemText primary={v} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tooltip title="Refresh data">
              <Box
                component="span"
                role="button"
                tabIndex={isRefreshing ? -1 : 0}
                onClick={isRefreshing ? undefined : handleRefreshClick}
                onKeyDown={(e) => {
                  if (isRefreshing) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRefreshClick();
                  }
                }}
                sx={{
                  ...actionTextSx,
                  opacity: isRefreshing ? 0.6 : 1,
                  pointerEvents: isRefreshing ? 'none' : 'auto',
                }}
              >
                {isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                Refresh
              </Box>
            </Tooltip>
            <GlobalDenseModeToolbar
              denseMode={denseMode}
              denseModeTogglePending={denseModeTogglePending}
              persistDenseMode={persistDenseMode}
            />
            <Tooltip title="Show/Hide Columns">
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={handleColumnMenuOpen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleColumnMenuOpen(e);
                  }
                }}
                sx={actionTextSx}
              >
                <ViewColumnIcon fontSize="small" />
                Columns
              </Box>
            </Tooltip>
            <Menu
              anchorEl={columnMenuAnchorEl}
              open={Boolean(columnMenuAnchorEl)}
              onClose={handleColumnMenuClose}
              PaperProps={{ sx: { maxHeight: '70vh', minWidth: 220, p: 1 } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show/Hide Columns</Typography>
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={handleColumnMenuClose}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleColumnMenuClose();
                    }
                  }}
                  sx={actionTextSx}
                >
                  <CloseIcon fontSize="small" />
                  Close
                </Box>
              </Box>
              {allKeys.map((k) => (
                <MenuItem key={k} onClick={() => toggleColumn(k)} sx={{ fontSize: '0.875rem', minHeight: 'auto', py: 0.75, px: 1 }}>
                  <Checkbox checked={visibleColumns[k] !== false} size="small" sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={toHeader(k)} primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.2 } }} />
                </MenuItem>
              ))}
            </Menu>
            <Tooltip title="Download CSV">
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={handleDownloadCSV}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleDownloadCSV();
                  }
                }}
                sx={actionTextSx}
              >
                <SaveAltIcon fontSize="small" />
                Download
              </Box>
            </Tooltip>
            </Box>
          </Box>
        </Box>

        <Paper elevation={2} sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {error ? <Box sx={{ p: 1, color: 'error.main', fontSize: '0.8125rem' }}>{String(error?.message || error)}</Box> : null}
          <HoverExpandDataGridPro
            rows={gridRows}
            columns={gridColumns}
            loading={isLoading}
            disableVirtualization
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10, 25, 50, 100, 1000, { value: -1, label: 'All' }]}
            initialState={{ pagination: { paginationModel: { pageSize: 1000, page: 0 } } }}
            rowHeight={denseMode ? 36 : 52}
            columnHeaderHeight={denseMode ? 40 : 56}
            sx={{ flex: 1, minHeight: 0 }}
          />
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default HomedepotProductsErrors;
